import React, { type ReactNode, useCallback, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags';
import type { PopupProps } from '@atlaskit/popup';
import {
	TeamsAppAnalyticsContext,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import { PopupTriggerWithHover as PopupTriggerWithHoverFromPackage } from '@atlaskit/teams-app-internal-popup-adaptor/popup-trigger-with-hover';
import { layers } from '@atlaskit/theme/constants';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { firedAt } from '../analytics';
import { useFireTriggeredRequest } from '../hooks/useProfileCardAnalytics';
import {
	entryPoint,
	type UserProfileCardRelayTriggerParams,
} from '../network/userProfileCard.entrypoint';
import {
	PopupTriggerWithHover as PopupTriggerWithHoverLocal,
	type TriggerMode,
} from '../popup-adapter/PopupTriggerWithHover';
import { CardOpenTimerProvider, useCardOpenTimer } from '../store/cardOpenTimer';
import { UserProfileCardContext } from '../store/context';
import type { TownsquareApp } from '../store/context';
import type { UserProfileCardAction } from '../types';
import { makeGetTestId } from '../utils/testId';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import { UserProfileCardTrigger } from './components/UserProfileCardTrigger';
import type { UserProfileCardRelayAgentProps } from './UserProfileCardRelay';

/**
 * Consumer-facing types re-exported so callers of the `./trigger` subpath can import them
 * without reaching into the package internals.
 */
export type { UserProfileCardAction } from '../types';

/**
 * Props for {@link UserProfileCardWithTrigger}. Extends {@link UserProfileCardRelayTriggerParams}
 * with popup placement, trigger mode, render target, and the trigger element children.
 */
export type UserProfileCardWithTriggerProps = UserProfileCardRelayTriggerParams &
	Pick<PopupProps, 'placement'> & {
		/** Hover, click, or preload-on-hover. Defaults to `'click'`. */
		trigger?: TriggerMode;
		/** When `true`, renders the popup into the document body via a portal. */
		shouldRenderToParent?: boolean;
		/** Element that the popup attaches to (the visible trigger). */
		children: ReactNode;
		/** Test id forwarded to the popup root. */
		testId?: string;
		/** Identifies the Townsquare app rendering this card (for XPC MAU tracking). */
		townsquareApp?: TownsquareApp;
		/**
		 * Called when the "View profile" button is clicked, in addition to the default navigation
		 * (e.g. for analytics). It does not replace the default navigation behaviour.
		 */
		onProfileClick?: (userId: string) => void;
		/** Agent-card config. Required for agent subjects. Omitting renders `CardError` if the subject is an agent. */
		agent?: UserProfileCardRelayAgentProps;
		/**
		 * Consumer-supplied custom actions exposed via the action menu in the
		 * top-right of the card header. Mirrors the `actions` prop on legacy `@atlaskit/profilecard`.
		 */
		actions?: UserProfileCardAction[];
	};

/**
 * Opens the user profile card in a popup (hover / click / preload) using the same Relay entry-point
 * pattern as `TeamProfileCardWithTrigger` (`@atlassian/team-profilecard/trigger`): preload runs
 * `UserProfileCardEntryPointQuery` (`knowledgeDiscovery.singleUser` + `activity.workedOn`).
 *
 * For a presentational card with data you already have, use {@link UserProfileCard} directly.
 */
export function UserProfileCardWithTrigger(
	props: UserProfileCardWithTriggerProps,
): React.JSX.Element {
	return (
		<AtlassianRelayEnvironmentProvider>
			<UserProfileCardWithTriggerInner {...props} />
		</AtlassianRelayEnvironmentProvider>
	);
}

/** Inner implementation of {@link UserProfileCardWithTrigger}. */
function UserProfileCardWithTriggerInner({
	userId,
	cloudId,
	orgId,
	viewerId,
	trigger,
	placement = 'bottom-start',
	shouldRenderToParent = false,
	children,
	testId,
	townsquareApp,
	onProfileClick,
	agent,
	actions,
}: UserProfileCardWithTriggerProps): React.JSX.Element {
	const PopupTriggerWithHover = fg('people-teams_migrate-popup-adaptor-package')
		? PopupTriggerWithHoverFromPackage
		: PopupTriggerWithHoverLocal;
	const resolvedTriggerMode = trigger ?? 'click';

	const entryPointErrorFallback = useCallback(
		(_props: { error: Error; onClose: () => void }) => <CardError testId={testId} />,
		[testId],
	);

	const getTestId = useMemo(() => makeGetTestId(userId, testId), [userId, testId]);

	// `triggerMethod` is the actual open method ('click' | 'hover') reported by the popup
	// adapter. It stays `undefined` until the first open so we never report a trigger-mode
	// value (e.g. 'preload-on-hover') as if it were an open method. This is hoisted onto
	// `TeamsAppAnalyticsContext.attributes` below so every analytics event fired beneath
	// the popup picks it up automatically without each event having to attach it manually.
	const [triggerMethod, setTriggerMethod] = useState<'click' | 'hover' | undefined>(undefined);

	const contextValue = useMemo(
		() => ({
			cloudId,
			townsquareApp,
			onProfileClick,
			getTestId,
		}),
		[cloudId, townsquareApp, onProfileClick, getTestId],
	);

	const analyticsContextData = useMemo(
		() => ({
			source: 'userProfileCard' as const,
			attributes: {
				cloudId,
				userId,
				viewerId,
				// Only attach `method` once an actual open has occurred so SLO dashboards never
				// see a synthetic method on pre-open events (e.g. preload).
				...(triggerMethod !== undefined ? { method: triggerMethod } : {}),
			},
		}),
		[cloudId, viewerId, userId, triggerMethod],
	);

	if (!userId || !cloudId || !orgId || !viewerId) {
		return <>{children}</>;
	}

	return (
		<ErrorBoundary fallback={<>{children}</>}>
			<UserProfileCardContext.Provider value={contextValue}>
				<TeamsAppAnalyticsContext data={analyticsContextData}>
					<CardOpenTimerProvider>
						<UserProfileCardPopupWithAnalytics
							PopupTriggerWithHover={PopupTriggerWithHover}
							entryPointParams={{ userId, cloudId, orgId, viewerId }}
							entryPointProps={{
								cloudId,
								orgId,
								viewerId,
								testId,
								agent,
								actions,
							}}
							placement={placement}
							shouldRenderToParent={shouldRenderToParent}
							triggerMode={resolvedTriggerMode}
							errorFallback={entryPointErrorFallback}
							onTriggerMethodChange={setTriggerMethod}
							trigger={(triggerProps) => (
								<UserProfileCardTrigger trigger={resolvedTriggerMode} triggerProps={triggerProps}>
									{children}
								</UserProfileCardTrigger>
							)}
						/>
					</CardOpenTimerProvider>
				</TeamsAppAnalyticsContext>
			</UserProfileCardContext.Provider>
		</ErrorBoundary>
	);
}

function UserProfileCardPopupWithAnalytics({
	PopupTriggerWithHover,
	entryPointParams,
	entryPointProps,
	placement,
	shouldRenderToParent,
	triggerMode,
	errorFallback,
	onTriggerMethodChange,
	trigger,
}: {
	/**
	 * The active `PopupTriggerWithHover` implementation, resolved by the parent
	 * based on the `people-teams_migrate-popup-adaptor-package` feature gate.
	 * Passing this in (rather than re-deriving it here) keeps a single source of
	 * truth for the gate lookup.
	 */
	PopupTriggerWithHover:
		| typeof PopupTriggerWithHoverFromPackage
		| typeof PopupTriggerWithHoverLocal;
	entryPointParams: UserProfileCardRelayTriggerParams;
	entryPointProps: {
		cloudId: string;
		orgId: string;
		viewerId: string;
		testId?: string;
		agent?: UserProfileCardRelayAgentProps;
		actions?: UserProfileCardAction[];
	};
	placement: PopupProps['placement'];
	shouldRenderToParent: boolean;
	triggerMode: TriggerMode;
	errorFallback: (props: { error: Error; onClose: () => void }) => React.JSX.Element;
	/** Bubbles the trigger method up to the parent so it can be exposed via UserProfileCardContext. */
	onTriggerMethodChange: (method: 'click' | 'hover') => void;
	trigger: React.ComponentProps<
		typeof PopupTriggerWithHoverFromPackage | typeof PopupTriggerWithHoverLocal
	>['trigger'];
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	const { markInteractionStart } = useCardOpenTimer();
	const fireTriggeredRequest = useFireTriggeredRequest();

	const handleOpen = useCallback(
		(method: 'click' | 'hover') => {
			// t=0 for all downstream `duration` measurements.
			markInteractionStart();
			onTriggerMethodChange(method);
			// `method` is attached inline because the context update from
			// `onTriggerMethodChange` has not propagated yet at this exact moment.
			fireEvent('ui.profilecard.triggered', { firedAt: firedAt(), method });
			fireTriggeredRequest();
		},
		[fireEvent, fireTriggeredRequest, markInteractionStart, onTriggerMethodChange],
	);

	return (
		<PopupTriggerWithHover
			entryPoint={entryPoint}
			placement={placement}
			fallback={<CardLoading testId={entryPointProps.testId ?? ''} />}
			errorFallback={errorFallback}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			shouldRenderToParent={shouldRenderToParent}
			shouldUseCaptureOnOutsideClick={true}
			zIndex={layers.modal()}
			triggerMode={triggerMode}
			onOpen={handleOpen}
			trigger={trigger}
		/>
	);
}
